/**
 * Profile360 Main Service
 * Orchestrates the entire Profile360 system with API-based architecture
 */

import apiService from './apiService';
import queryRouter from './queryRouter';
import responseProcessor from './responseProcessor';
import cacheService from './cacheService';
import errorRecoveryService from './errorRecoveryService';

/**
 * Main Profile360 Service class
 */
class Profile360Service {
  constructor() {
    this.apiService = apiService;
    this.queryRouter = queryRouter;
    this.responseProcessor = responseProcessor;
    this.cacheService = cacheService;
    this.errorRecoveryService = errorRecoveryService;
    this.isInitialized = false;
  }

  /**
   * Initialize the service with authentication and tenant info
   * @param {string} authToken - JWT authentication token
   * @param {string} tenantName - Tenant identifier
   */
  initialize(authToken, tenantName) {
    this.apiService.refreshConfig(); // Ensure we have the latest config
    this.apiService.setAuthToken(authToken);
    this.apiService.setTenant(tenantName);
    this.isInitialized = true;
  }

  /**
   * Check if service is properly initialized
   * @throws {Error} If not initialized
   */
  ensureInitialized() {
    if (!this.isInitialized) {
      throw new Error('Profile360Service must be initialized with auth token and tenant');
    }
  }

  /**
   * Execute a natural language query with caching and error recovery
   * @param {string} query - Natural language query
   * @returns {Promise<Object>} Processed query results
   */
  async executeQuery(query) {
    this.ensureInitialized();

    // Check cache first
    const cachedResult = this.cacheService.get(query);
    if (cachedResult) {
      return cachedResult;
    }

    try {
      // Execute with error recovery
      const result = await this.errorRecoveryService.executeWithRetry(async () => {
        // Route the query to appropriate API endpoint
        const apiResponse = await this.queryRouter.routeQuery(query);

        if (!apiResponse.success) {
          throw new Error(apiResponse.error);
        }

        // Process the response for visualization
        const processedResponse = this.responseProcessor.processResponse(apiResponse);

        return {
          ...processedResponse,
          query,
          route: apiResponse.route,
          originalData: apiResponse.data
        };
      });

      // Cache successful results
      if (result.success) {
        this.cacheService.set(query, {}, result);
      }

      return result;

    } catch (error) {
      // Try graceful degradation
      return this.errorRecoveryService.handleGracefulDegradation(query, error);
    }
  }

  /**
   * Get sample queries for the UI
   * @returns {Promise<Array>} Sample queries
   */
  async getSampleQueries() {
    // Fixed 4 samples in this specific order (no try/catch to avoid unreachable-code warning)
    return [
      'give insights of pradeep@trustxinc.com',
      'profile Henry Evans',
      'Show me all data',
      'show me all users'
    ];
  }

  /**
   * Get database health status
   * @returns {Promise<Object>} Health status
   */
  async getHealthStatus() {
    this.ensureInitialized();
    
    try {
      const response = await this.apiService.getKnowledgeGraphHealth();
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get database statistics
   * @returns {Promise<Object>} Database statistics
   */
  async getDatabaseStats() {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getDatabaseStats();
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse({
          ...apiResponse,
          metadata: { response_type: 'table' }
        });
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get database schema
   * @returns {Promise<Object>} Database schema
   */
  async getDatabaseSchema() {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getDatabaseSchema();
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse({
          ...apiResponse,
          metadata: { response_type: 'table' }
        });
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Execute a specific user query
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User graph data
   */
  async getUserGraph(userId) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getUserKnowledgeGraph(userId);
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse({
          ...apiResponse,
          metadata: { response_type: 'graph' }
        });
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get dormant users
   * @returns {Promise<Object>} Dormant users data
   */
  async getDormantUsers() {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getDormantUsers();
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse({
          ...apiResponse,
          metadata: { response_type: 'table' }
        });
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Apply filters to current data
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Filtered results
   */
  async applyFilters(filters) {
    this.ensureInitialized();

    try {
      const apiResponse = await this.apiService.applyGraphFilters(filters);
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse(apiResponse);
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get stale apps for a specific user
   * @param {string} userEmail - User email address
   * @param {number} days - Number of days to consider as stale threshold
   * @returns {Promise<Object>} Stale apps data
   */
  async getStaleAppsForUser(userEmail, days = 30) {
    this.ensureInitialized();

    try {
      const apiResponse = await this.apiService.getStaleAppsForUser(userEmail, days);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all available users
   * @returns {Promise<Object>} Users list
   */
  async getAllUsers() {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getAllUsers();
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse({
          ...apiResponse,
          metadata: { response_type: 'table' }
        });
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all available applications
   * @returns {Promise<Object>} Applications list
   */
  async getAllApplications() {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getAllApplications();
      if (apiResponse.success) {
        const processedResponse = this.responseProcessor.processResponse({
          ...apiResponse,
          metadata: { response_type: 'table' }
        });
        return processedResponse;
      }
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ===== AI-Powered Dormant User Insights =====

  /**
   * Get AI-generated dormant user insights with filtering
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} AI insights data
   */
  async getAIDormantInsights(filters = {}) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getAIDormantInsights(filters);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get AI insight for a specific user
   * @param {string} userEmail - User email address
   * @returns {Promise<Object>} User AI insight data
   */
  async getUserAIInsight(userEmail) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getUserAIInsight(userEmail);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Trigger AI analysis (Admin only - use sparingly)
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Analysis trigger response
   */
  async triggerAIAnalysis(options = {}) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.triggerAIAnalysis(options);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get AI analysis history
   * @param {number} limit - Maximum number of results
   * @returns {Promise<Object>} Analysis history data
   */
  async getAnalysisHistory(limit = 10) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getAnalysisHistory(limit);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get risk distribution statistics
   * @returns {Promise<Object>} Risk distribution data
   */
  async getRiskDistribution() {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getRiskDistribution();
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get high-risk dormant users for alerts
   * @param {Object} options - Query options
   * @returns {Promise<Object>} High-risk users data
   */
  async getHighRiskUsers(options = {}) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getHighRiskUsers(options);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get comprehensive dormant analytics for a specific user
   * @param {number} userId - User ID
   * @param {number} daysThreshold - Days threshold for dormancy calculation (default: 90)
   * @returns {Promise<Object>} User dormant analytics data
   */
  async getUserDormantAnalytics(userId, daysThreshold = 90) {
    this.ensureInitialized();
    
    try {
      const apiResponse = await this.apiService.getUserDormantAnalytics(userId, daysThreshold);
      return apiResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Test the service connectivity
   * @returns {Promise<Object>} Test results
   */
  async testConnectivity() {
    try {
      const healthCheck = await this.getHealthStatus();
      return {
        success: healthCheck.success,
        message: healthCheck.success ? 'Profile360 service is operational' : 'Service connectivity issues',
        details: healthCheck
      };
    } catch (error) {
      return {
        success: false,
        message: 'Service connectivity test failed',
        error: error.message
      };
    }
  }

  /**
   * Reset the service (clear cache, reset state)
   */
  reset() {
    this.responseProcessor.nodeIdCounter = 0;
    this.responseProcessor.linkIdCounter = 0;
  }
}

// Create singleton instance
const profile360Service = new Profile360Service();

export default profile360Service;
